import classNames from 'classnames';
import React from 'react';
import { MessageDescriptor } from 'react-intl';
import { IReservationModel } from '../../../entities/reservation';
import { StepType } from '../../../services/getStepOrder';
import StepNumber from '../StepNumber/StepNumber';
import StepValue from '../StepValue/StepValue';
import JumpToEnd, { PendingJumpType } from '../JumpToEnd/JumpToEnd';
import { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Routes } from 'src/routes';
import globalMessages from 'src/services/globalMessages';
import { IStepModuleProps } from 'src/models/modules';
import { motion } from 'framer-motion';
import { isAppointmentCalendar } from 'src/entities/calendar';
import { scrollToStep } from 'src/services/scrollerFunctions';
import { BookingContext, IBookingFlowProvidedState } from 'src/contexts/BookingFlowContext';

export interface IStepState {
    isModifiable: boolean;
}

export interface IStepProps {
    isActive: boolean;
    isCompleted: boolean;
    isLast: boolean;
    isProgressBarHidden?: boolean;
    name: MessageDescriptor;
    content: React.FC<IStepModuleProps>;
    step: StepType;
    stepIndex: number;
    nextHandler: () => void;
    jumpToEndHandler: () => void;
    modifyHandler: (stepIndex: number) => void;
}

export const getStepSelectedValue = (step: StepType, reservation: IReservationModel = {}): string | MessageDescriptor => {
    if (step === StepType.Calendar && !reservation[step]) {
        return globalMessages.whichever
    }
    const currentStep = step as keyof Omit<IReservationModel, 'searchParams'>;
    return reservation[currentStep] && reservation[currentStep]?.displayValue || '';
}

const isEverySearchParamSet = (searchParams: IReservationModel['searchParams']) =>
    searchParams && !Object.values(searchParams).includes('unset')

const variants: React.ComponentProps<typeof motion.div>['variants'] = {
    visible: { opacity: 1, height: 'auto', overflow: 'hidden', transitionEnd: { overflow: 'visible' }, transition: { duration: 1 } },
    hidden: { opacity: 0, height: 0, overflow: 'hidden', transitionEnd: { overflow: 'hidden' }, transition: { duration: 0.5 } },
}

const Navigator: React.FunctionComponent<{ isOpen: boolean, index: number }> = ({ isOpen, index }) => {
    useEffect(() => {
        if (isOpen && index > 0) {
            setTimeout(() => {
                scrollToStep(index);
            }, 0);
        }
    }, [isOpen, index])

    return null;
}

const Step: React.FunctionComponent<IStepProps> = (
    { content, name, nextHandler, isActive, step, modifyHandler, isCompleted, stepIndex, isLast, isProgressBarHidden, jumpToEndHandler }
) => {

    const context: IBookingFlowProvidedState = useContext(BookingContext);
    const location = useLocation();
    const navigate = useNavigate();
    const isHomeSlashRoute = location.pathname === Routes.HomeSlash;

    const [isModifiable, setIsModifiable] = useState(true);

    const [isLoading, setIsLoading] = useState(false);

    const reservationData = context.reservations[context.activeReservationIndex] || {
        searchParams: {
            CalendarId: 'unset',
            ServiceId: 'unset',
            Date: 'unset'
        }
    };

    const bookingStepClasses = classNames(
        'booking-step',
        { 'booking-step--touched': isActive || isCompleted },
        { 'booking-step--progress-bar-hidden': isProgressBarHidden },
        {
            'booking-step--full-width':
                step === StepType.Timetable
                || step === StepType.Date && isAppointmentCalendar(reservationData[StepType.Calendar])
        },
        { 'booking-step--last': isLast }
    )

    const selectedValue = getStepSelectedValue(step, reservationData);
    const showReservationsNumber = context.reservations.length > 1 && isLast;

    const onCompleteHandler = (value: IReservationModel, hasSkipped?: boolean) => {
        if (!hasSkipped && isHomeSlashRoute) {
            navigate({
                pathname: Routes.Book,
                search: location.search
            })
        }

        context.actions.setReservationModel(step, value);
        if (!context.hasAutoSelected && isEverySearchParamSet(value.searchParams)) {
            context.actions.autoSelected();
        }
        setIsModifiable(!hasSkipped)
        nextHandler();
    }

    const isOpen = isActive && !isLoading;

    return (
        <article className={bookingStepClasses}>
            <Navigator index={stepIndex} isOpen={isOpen} />
            {isProgressBarHidden || <StepNumber name={name} index={stepIndex + 1} touched={isActive || isCompleted} isLoading={isActive && isLoading} />}
            <div className={classNames('booking-step__body', { 'booking-step__body--open': isOpen })}>
                <motion.div
                    initial={isOpen ? 'visible' : 'hidden'}
                    variants={variants}
                    animate={isOpen ? 'visible' : 'hidden'}
                >
                    <div className="booking-step__module-wrapper">
                        {content({
                            isActive,
                            isFirst: stepIndex === 0,
                            data: reservationData,
                            onComplete: onCompleteHandler,
                            onIsModifiable: setIsModifiable,
                            onLoadingChanged: setIsLoading
                        })}
                    </div>
                </motion.div>
                {(isCompleted && !isActive || showReservationsNumber) &&
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.3 }}
                        className="booking-step__value"
                    >
                        {showReservationsNumber
                            ? <JumpToEnd type={PendingJumpType.Bottom} action={jumpToEndHandler} reservationCount={context.reservations.length - 1} />
                            : <StepValue
                                selectedValue={selectedValue}
                                action={() => modifyHandler(stepIndex)}
                                isModifiable={isModifiable}
                            />
                        }
                    </motion.div>
                }
            </div>
        </article>
    );
}

export default Step;
